/**
 * useStockfish - Client-side Stockfish chess engine hook
 *
 * Uses Stockfish 17.1 WASM (lite single-threaded version) for:
 * - Fully offline play
 * - Mobile-friendly (~7MB)
 * - Configurable difficulty via depth/time limits
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Difficulty presets (depth = how many moves ahead to calculate)
export const DIFFICULTY_PRESETS = {
    beginner: { depth: 1, moveTime: 100 },      // ~800 ELO
    easy: { depth: 3, moveTime: 300 },          // ~1000 ELO
    medium: { depth: 8, moveTime: 1000 },       // ~1400 ELO
    hard: { depth: 12, moveTime: 2000 },        // ~1800 ELO
    expert: { depth: 16, moveTime: 5000 },      // ~2200 ELO
    master: { depth: 20, moveTime: 10000 },     // ~2600+ ELO (full strength)
} as const;

export type DifficultyLevel = keyof typeof DIFFICULTY_PRESETS;

export interface StockfishMove {
    from: string;
    to: string;
    promotion?: string;
}

export interface UseStockfishOptions {
    difficulty?: DifficultyLevel;
    onReady?: () => void;
    onError?: (error: string) => void;
}

export interface UseStockfishReturn {
    isReady: boolean;
    isThinking: boolean;
    isLoading: boolean;
    error: string | null;
    difficulty: DifficultyLevel;
    setDifficulty: (level: DifficultyLevel) => void;
    getBestMove: (fen: string) => Promise<StockfishMove | null>;
    stopThinking: () => void;
    getEvaluation: (fen: string) => Promise<number | null>;
}

const STOCKFISH_PATH = '/stockfish/stockfish-17.1-lite-single-03e3232.js';

export function useStockfish(options: UseStockfishOptions = {}): UseStockfishReturn {
    const {
        difficulty: initialDifficulty = 'medium',
        onReady,
        onError
    } = options;

    const [isReady, setIsReady] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [difficulty, setDifficultyState] = useState<DifficultyLevel>(initialDifficulty);

    const workerRef = useRef<Worker | null>(null);
    const resolveRef = useRef<((move: StockfishMove | null) => void) | null>(null);
    const evalResolveRef = useRef<((score: number | null) => void) | null>(null);

    // Initialize Stockfish Web Worker
    useEffect(() => {
        let mounted = true;

        const initStockfish = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Create web worker from stockfish.js
                const worker = new Worker(STOCKFISH_PATH);
                workerRef.current = worker;

                let bestMove: string | null = null;
                let evalScore: number | null = null;

                worker.onmessage = (e: MessageEvent) => {
                    const message = typeof e.data === 'string' ? e.data : e.data?.toString() || '';

                    // Engine ready
                    if (message === 'uciok') {
                        if (mounted) {
                            setIsReady(true);
                            setIsLoading(false);
                            onReady?.();
                        }
                    }

                    // Best move found
                    if (message.startsWith('bestmove')) {
                        const parts = message.split(' ');
                        const moveStr = parts[1];

                        if (moveStr && moveStr !== '(none)') {
                            bestMove = moveStr;
                            const move: StockfishMove = {
                                from: moveStr.substring(0, 2),
                                to: moveStr.substring(2, 4),
                                promotion: moveStr.length > 4 ? moveStr.substring(4, 5) : undefined
                            };

                            if (mounted) {
                                setIsThinking(false);
                                resolveRef.current?.(move);
                                resolveRef.current = null;
                            }
                        } else {
                            if (mounted) {
                                setIsThinking(false);
                                resolveRef.current?.(null);
                                resolveRef.current = null;
                            }
                        }
                    }

                    // Evaluation score (centipawns from white's perspective)
                    if (message.startsWith('info') && message.includes(' score cp ')) {
                        const match = message.match(/score cp (-?\d+)/);
                        if (match) {
                            evalScore = parseInt(match[1], 10) / 100; // Convert centipawns to pawns
                        }
                    }

                    // Mate score
                    if (message.startsWith('info') && message.includes(' score mate ')) {
                        const match = message.match(/score mate (-?\d+)/);
                        if (match) {
                            const mateIn = parseInt(match[1], 10);
                            evalScore = mateIn > 0 ? 100 : -100; // Large value for mate
                        }
                    }
                };

                worker.onerror = (e) => {
                    console.error('Stockfish worker error:', e);
                    if (mounted) {
                        setError('Failed to load chess engine');
                        setIsLoading(false);
                        onError?.('Failed to load chess engine');
                    }
                };

                // Initialize UCI protocol
                worker.postMessage('uci');

            } catch (err) {
                console.error('Failed to initialize Stockfish:', err);
                if (mounted) {
                    setError('Failed to initialize chess engine');
                    setIsLoading(false);
                    onError?.('Failed to initialize chess engine');
                }
            }
        };

        initStockfish();

        return () => {
            mounted = false;
            if (workerRef.current) {
                workerRef.current.postMessage('quit');
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, [onReady, onError]);

    // Get best move for a position
    const getBestMove = useCallback(async (fen: string): Promise<StockfishMove | null> => {
        if (!workerRef.current || !isReady) {
            console.warn('Stockfish not ready');
            return null;
        }

        setIsThinking(true);

        const preset = DIFFICULTY_PRESETS[difficulty];

        return new Promise((resolve) => {
            resolveRef.current = resolve;

            const worker = workerRef.current!;

            // Set position
            worker.postMessage(`position fen ${fen}`);

            // Search with depth and time limit
            worker.postMessage(`go depth ${preset.depth} movetime ${preset.moveTime}`);

            // Timeout fallback (in case engine gets stuck)
            setTimeout(() => {
                if (resolveRef.current === resolve) {
                    setIsThinking(false);
                    resolve(null);
                    resolveRef.current = null;
                }
            }, preset.moveTime + 5000);
        });
    }, [isReady, difficulty]);

    // Stop current calculation
    const stopThinking = useCallback(() => {
        if (workerRef.current && isThinking) {
            workerRef.current.postMessage('stop');
            setIsThinking(false);
            resolveRef.current?.(null);
            resolveRef.current = null;
        }
    }, [isThinking]);

    // Get position evaluation (for analysis)
    const getEvaluation = useCallback(async (fen: string): Promise<number | null> => {
        if (!workerRef.current || !isReady) {
            return null;
        }

        return new Promise((resolve) => {
            evalResolveRef.current = resolve;

            const worker = workerRef.current!;
            worker.postMessage(`position fen ${fen}`);
            worker.postMessage('go depth 10 movetime 500');

            // Listen for evaluation in the message handler (already set up)
            setTimeout(() => {
                resolve(null);
                evalResolveRef.current = null;
            }, 1000);
        });
    }, [isReady]);

    // Set difficulty
    const setDifficulty = useCallback((level: DifficultyLevel) => {
        setDifficultyState(level);
    }, []);

    return {
        isReady,
        isThinking,
        isLoading,
        error,
        difficulty,
        setDifficulty,
        getBestMove,
        stopThinking,
        getEvaluation
    };
}

export default useStockfish;
